import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { format, isSameDay } from 'date-fns';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import { BillAccountsSelectors } from '@amfam/billing/billaccount/data-access';
import { ScheduledPaymentSelectors } from '@amfam/billing/schedulepayment/data-access';
import {
  PaymentTermsAndConditionsPartnerContent,
  PaymentTermsAndConditionsType
} from '@amfam/billing/shared/ui';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { BillingAnalytics, ScheduledPayment } from '@amfam/shared/models';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import {
  Applications,
  ApplicationService,
  CopyService
} from '@amfam/shared/utility/shared-services';
import {
  AlertModalConfig,
  DockingBarService,
  DsModalService,
  LoadingSpinnerService
} from '@amfam/ui-kit';

import { OneTimePayFeatureFacade } from '../../+state/one-time-pay-feature.facade';
import {
  PaymentMethodRelatedPaymentErrorCodes,
  simplifiedPaymentReviewData
} from '../../+state/one-time-pay-feature.models';

@Component({
  selector: 'amfam-payment-review-wrapper',
  templateUrl: './payment-review-wrapper.component.html',
  styleUrls: ['./payment-review-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PaymentReviewWrapperComponent implements OnInit, OnDestroy {
  simplifiedPaymentInfo$: Observable<simplifiedPaymentReviewData>;
  isMyAccountAdmin = this.appService.isApp(Applications.MYACCOUNT_ADMIN);
  showTermsAndConditionsModal = false;
  isPartner$: Observable<boolean>;
  termsAndConditionsType: PaymentTermsAndConditionsType;
  termsAndConditionsPartnerContent$: Observable<PaymentTermsAndConditionsPartnerContent>;
  heading = this.copyService.getCopy('billing.simplifiedPayment.paymentReviewHeading');
  paymentErrorHeading = this.copyService.getCopy('billing.simplifiedPayment.paymentErrorHeading');
  goBackText = this.copyService.getCopy('billing.simplifiedPayment.paymentReviewGoBackText');
  cancelModalConfig: AlertModalConfig = this.oneTimePayFacade.cancelModalConfig();
  isDisabledAfterClick = false;
  paymentError$: Observable<string>;
  analyticsSend = false;
  scheduledPayments: ScheduledPayment[];
  dupPayments: ScheduledPayment[] = [];
  isPhoenix = false;
  isHomesite$: Observable<boolean>;

  // Shared subject for completing observables
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private appService: ApplicationService,
    private dockingBar: DockingBarService,
    private copyService: CopyService,
    private modalService: DsModalService,
    public roleService: ImpersonateRolesService,
    private spinner: LoadingSpinnerService,
    private oneTimePayFacade: OneTimePayFeatureFacade,
    private store: Store,
    private analyticsFacade: AnalyticsFacade
  ) {}

  ngOnInit(): void {
    this.dockingBar.registerHeading(
      this.copyService.getCopy('billing.simplifiedPayment.dockingBarHeading')
    );
    this.simplifiedPaymentInfo$ = this.oneTimePayFacade.getPaymentReviewInfo();
    this.termsAndConditionsType = this.oneTimePayFacade.getPaymentTermsAndConditionsType();
    this.isPartner$ = this.oneTimePayFacade.isPartner();
    this.termsAndConditionsPartnerContent$ =
      this.oneTimePayFacade.getPaymentTermsAndConditionsPartnerContent();
    this.paymentError$ = this.getPaymentError();
    setTimeout(() => {
      this.oneTimePayFacade.sendAnalytics({ pageAnalyticName: 'PaymentReview' });
    }, 0);

    combineLatest([this.store.select(ScheduledPaymentSelectors.selectScheduledPayments)])
      .pipe(take(1))
      .subscribe(([scheduledPayments]) => {
        this.scheduledPayments = scheduledPayments;
        this.spinner.stop();
      });

    this.oneTimePayFacade
      .selectIsPhoenixPolicy()
      .pipe(
        map(data => data),
        takeUntil(this.stop$)
      )
      .subscribe(data => {
        this.isPhoenix = data;
      });

    this.isHomesite$ = this.store.select(BillAccountsSelectors.selectIsSelectedBillPolicyHomesite);
  }

  agreeTermsAndConditionsEvent(agree: boolean): void {
    if (agree) {
      this.submit();
    }
  }

  goBack() {
    this.oneTimePayFacade.goBackToAccountSelectionPage();
    this.oneTimePayFacade.sendAnalytics({ buttonAnalyticName: 'Simplified:ReviewPayment:Edit' });
  }

  preSubmit() {
    if (this.isMyAccountAdmin) {
      this.showTermsAndConditionsModal = true;
    } else {
      this.submit();
      this.isDisabledAfterClick = true;
    }
  }

  submit() {
    const otpReviewData = this.oneTimePayFacade.preprocessPaymentPayload();

    const todaysDate = format(new Date(), 'YYYY/MM/DD');
    let duplicatePayment = false;
    this.dupPayments = [];

    otpReviewData.forEach(selectedAccount => {
      const sameDaySameAmtPayment = this.scheduledPayments.find(
        (scheduledPayment: ScheduledPayment) =>
          scheduledPayment.billAccounts[0].billAccountNumber ===
            selectedAccount.billAccountNumber &&
          parseFloat(scheduledPayment.billAccounts[0].billAcctPaymentAmount) ===
            selectedAccount.selectedPaymentAmount &&
          isSameDay(todaysDate, scheduledPayment.receivedDate)
      );

      if (sameDaySameAmtPayment) {
        duplicatePayment = true;
        this.dupPayments.push(sameDaySameAmtPayment);
      }
    });

    if (duplicatePayment) {
      // Open Modal
      this.analyticsFacade.trackButtonClick(BillingAnalytics.payNowClicked);
      this.modalService.open('duplicatePaymentConfirmationModal');
      setTimeout(() => {
        this.analyticsFacade.trackPage(BillingAnalytics.duplicatePaymentConfirmationModalOpen);
      }, 1);
    } else {
      this.proceedForPayment(false);
    }
  }

  handleModalYes() {
    this.analyticsFacade.trackButtonClick(BillingAnalytics.yesClicked);
    this.modalService.close('duplicatePaymentConfirmationModal');
    this.proceedForPayment(true);
  }

  handleModalNo() {
    // Enable the pay now here
    this.isDisabledAfterClick = false;
    this.analyticsFacade.trackButtonClick(BillingAnalytics.noClicked);
    this.modalService.close('duplicatePaymentConfirmationModal');
  }

  proceedForPayment(fromDuplicateModal: boolean) {
    this.spinner.start({
      blockActions: true
    });

    const otpReviewData = this.oneTimePayFacade.preprocessPaymentPayload();
    this.oneTimePayFacade.addOTPReviewInfo(otpReviewData);
    this.oneTimePayFacade.submitOTP();
    if (!fromDuplicateModal) {
      this.oneTimePayFacade.sendAnalytics({
        buttonAnalyticName: 'Simplified:ReviewPayment:PayNow:Complete'
      });
    }
  }

  cancel() {
    this.modalService.open(this.cancelModalConfig.modalId);
    this.oneTimePayFacade.sendAnalytics({ buttonAnalyticName: 'Simplified:ReviewPayment:Cancel' });
  }

  stay() {
    this.modalService.close(this.cancelModalConfig.modalId);
  }

  leave() {
    this.modalService.closeAll();
    this.oneTimePayFacade.leaveOTPFlow();
  }

  getPaymentError(): Observable<string> {
    return this.oneTimePayFacade.getPaymentErrorCode().pipe(
      map(errorCode => {
        if (PaymentMethodRelatedPaymentErrorCodes.includes(errorCode)) {
          this.spinner.stop();
          this.isDisabledAfterClick = false;
          if (!this.analyticsSend) {
            this.oneTimePayFacade.sendAnalytics({
              pageAnalyticName: 'Simplified:Error',
              pageAnalyticSubCategory3: errorCode
            });
            this.analyticsSend = true;
          }
          return this.copyService.getCopy(
            `billing.paymentFailureVagueErrorMessages.body.${errorCode}`
          );
        }
      })
    );
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
